import { useState, useEffect, useMemo } from 'react'
import { adminApi } from '../../services/api'
import { useAuth } from '../../hooks/useAuth'
import LoadingSpinner from './LoadingSpinner'

interface PaidGolfer {
  name: string
  email: string
  source: 'registration' | 'sponsor_foursome'
  teamName?: string
  sponsorName?: string
}

interface SendResult {
  email: string
  name: string
  status: 'sent' | 'failed' | 'skipped'
  error?: string
}

type AuctionEmailVariant = 'reminder' | 'final-hour'

interface SilentAuctionModalProps {
  golfers: PaidGolfer[]
  loadingGolfers: boolean
  onClose: () => void
  variant?: AuctionEmailVariant
}

const VARIANT_CONFIG: Record<AuctionEmailVariant, {
  title: string
  buttonLabel: string
  buttonClass: string
}> = {
  'reminder': {
    title: 'Send Silent Auction Email',
    buttonLabel: 'Send Email',
    buttonClass: 'bg-amber-600 hover:bg-amber-700',
  },
  'final-hour': {
    title: 'Send Final Hour Auction Reminder',
    buttonLabel: 'Send Final Hour Email',
    buttonClass: 'bg-orange-600 hover:bg-orange-700',
  },
}

export default function SilentAuctionModal({ golfers, loadingGolfers, onClose, variant = 'reminder' }: SilentAuctionModalProps) {
  const { getToken } = useAuth()
  const config = VARIANT_CONFIG[variant]
  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set())
  const [golferSearch, setGolferSearch] = useState('')
  const [sending, setSending] = useState(false)
  const [sendResults, setSendResults] = useState<SendResult[] | null>(null)
  const [confirmSend, setConfirmSend] = useState(false)

  // Select all golfers by default when they load
  useEffect(() => {
    if (golfers.length > 0) {
      setSelectedEmails(new Set(golfers.map((g) => g.email)))
    }
  }, [golfers])

  const filteredGolfers = useMemo(() => {
    if (!golferSearch.trim()) return golfers
    const q = golferSearch.toLowerCase()
    return golfers.filter(
      (g) => g.name.toLowerCase().includes(q) || g.email.toLowerCase().includes(q)
    )
  }, [golfers, golferSearch])

  const handleToggleAll = () => {
    const filteredEmails = filteredGolfers.map((g) => g.email)
    const allSelected = filteredEmails.every((e) => selectedEmails.has(e))
    const next = new Set(selectedEmails)
    if (allSelected) {
      filteredEmails.forEach((e) => next.delete(e))
    } else {
      filteredEmails.forEach((e) => next.add(e))
    }
    setSelectedEmails(next)
  }

  const handleToggleGolfer = (email: string) => {
    const next = new Set(selectedEmails)
    if (next.has(email)) {
      next.delete(email)
    } else {
      next.add(email)
    }
    setSelectedEmails(next)
  }

  const handleSendAuctionEmails = async () => {
    if (!confirmSend) {
      setConfirmSend(true)
      return
    }
    setSending(true)
    setSendResults(null)
    try {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')
      const recipients = golfers
        .filter((g) => selectedEmails.has(g.email))
        .map((g) => ({ email: g.email, name: g.name }))
      const data = variant === 'final-hour'
        ? await adminApi.sendSilentAuctionFinalHourEmail(token, recipients)
        : await adminApi.sendSilentAuctionEmail(token, recipients)
      setSendResults(data.results)
    } catch (err) {
      setSendResults([{
        email: '',
        name: '',
        status: 'failed',
        error: err instanceof Error ? err.message : 'Failed to send emails',
      }])
    } finally {
      setSending(false)
      setConfirmSend(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex justify-between items-center">
          <h2 className="text-lg font-bold text-gray-800">{config.title}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
          >
            &times;
          </button>
        </div>

        {loadingGolfers ? (
          <div className="p-12 text-center">
            <LoadingSpinner size="lg" />
            <p className="mt-2 text-gray-500">Loading golfers...</p>
          </div>
        ) : sendResults ? (
          /* Results view */
          <div className="p-4 overflow-y-auto flex-1">
            {(() => {
              const sent = sendResults.filter((r) => r.status === 'sent')
              const failed = sendResults.filter((r) => r.status === 'failed' || r.status === 'skipped')
              return (
                <>
                  <div className={`p-4 rounded-lg mb-4 ${failed.length > 0 ? 'bg-yellow-50 border border-yellow-200' : 'bg-green-50 border border-green-200'}`}>
                    <p className={`font-semibold ${failed.length > 0 ? 'text-yellow-800' : 'text-green-800'}`}>
                      {sent.length} of {sendResults.length} emails sent successfully
                      {failed.length > 0 && ` | ${failed.length} failed`}
                    </p>
                  </div>
                  {failed.length > 0 && (
                    <div className="mb-4">
                      <h3 className="font-semibold text-red-700 mb-2">Failed Emails:</h3>
                      <div className="space-y-2">
                        {failed.map((r, i) => (
                          <div key={i} className="bg-red-50 border border-red-200 rounded p-3 text-sm">
                            <p className="font-medium text-red-800">{r.name || 'Unknown'} ({r.email || 'N/A'})</p>
                            <p className="text-red-600 text-xs mt-1">{r.error}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {sent.length > 0 && (
                    <details className="mb-4">
                      <summary className="cursor-pointer text-sm text-green-700 font-medium">
                        View {sent.length} successfully sent
                      </summary>
                      <div className="mt-2 space-y-1 max-h-48 overflow-y-auto">
                        {sent.map((r, i) => (
                          <p key={i} className="text-sm text-gray-600">{r.name} ({r.email})</p>
                        ))}
                      </div>
                    </details>
                  )}
                </>
              )
            })()}
          </div>
        ) : (
          /* Selection view */
          <>
            {/* Search */}
            <div className="p-4 border-b">
              <input
                type="text"
                placeholder="Search by name or email..."
                className="input w-full"
                value={golferSearch}
                onChange={(e) => setGolferSearch(e.target.value)}
              />
            </div>

            {/* Select all + count */}
            <div className="px-4 py-2 border-b bg-gray-50 flex justify-between items-center">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filteredGolfers.length > 0 && filteredGolfers.every((g) => selectedEmails.has(g.email))}
                  onChange={handleToggleAll}
                  className="rounded border-gray-300"
                />
                <span className="text-sm font-medium text-gray-700">
                  Select All {golferSearch && `(filtered)`}
                </span>
              </label>
              <span className="text-sm text-gray-500">
                {selectedEmails.size} of {golfers.length} selected
              </span>
            </div>

            {/* Golfer list */}
            <div className="overflow-y-auto flex-1 divide-y" style={{ maxHeight: '400px' }}>
              {filteredGolfers.map((golfer) => (
                <label
                  key={golfer.email}
                  className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedEmails.has(golfer.email)}
                    onChange={() => handleToggleGolfer(golfer.email)}
                    className="rounded border-gray-300"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{golfer.name}</p>
                    <p className="text-xs text-gray-500 truncate">{golfer.email}</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded whitespace-nowrap ${
                    golfer.source === 'registration'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-purple-100 text-purple-700'
                  }`}>
                    {golfer.source === 'registration'
                      ? golfer.teamName || 'Registration'
                      : `Foursome: ${golfer.sponsorName || 'Sponsor'}`}
                  </span>
                </label>
              ))}
              {filteredGolfers.length === 0 && (
                <p className="text-center text-gray-400 py-8">No golfers found</p>
              )}
            </div>
          </>
        )}

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex justify-between items-center">
          {sendResults ? (
            <button
              onClick={onClose}
              className="btn btn-primary"
            >
              Close
            </button>
          ) : (
            <>
              <button
                onClick={onClose}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              {confirmSend ? (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700 font-medium">
                    Send to {selectedEmails.size} golfers?
                  </span>
                  <button
                    onClick={() => setConfirmSend(false)}
                    className="btn btn-secondary text-sm"
                  >
                    No
                  </button>
                  <button
                    onClick={handleSendAuctionEmails}
                    disabled={sending}
                    className={`btn ${config.buttonClass} text-white text-sm`}
                  >
                    {sending ? (
                      <span className="flex items-center gap-2">
                        <LoadingSpinner size="sm" />
                        Sending...
                      </span>
                    ) : (
                      'Yes, Send'
                    )}
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleSendAuctionEmails}
                  disabled={selectedEmails.size === 0}
                  className={`btn ${config.buttonClass} text-white disabled:opacity-50`}
                >
                  {config.buttonLabel} ({selectedEmails.size})
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
